'use server'

import { apiRequest, ApiResponse } from '../request'
import { getClient } from '../client'
import { deliverPdf, downloadBlob, PdfDelivery } from '../../download-blob'
import type { PayoutShare } from '@/lib/billing/financials'
import { getStoredToken } from '@/lib/api/session-refresh'
import type { ServiceAddressSnapshot } from '@/lib/api/common/service-address'

export type ExamStatus = 'BUDGET' | 'REQUESTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'

export interface ExamAttachment {
    fileName: string
    filePath: string
    userName: string
    description: string
    details?: string | null
    createdAt?: string
}

export interface ExamResult {
    id: number
    examId: number
    catalogId: number
    catalog?: {
        id: number
        name: string
        catalogType?: { id: number; name: string }
    }
    result?: string
    observations?: string
    resultDate: string
    attachments?: ExamAttachment[]
    professionalId: number
    professional?: {
        id: number
        name: string
        registrationNumber?: string
    }
    createdAt: string
    updatedAt: string
}

export interface ExamItem {
    id: number
    catalogId: number
    catalog: {
        id: number
        name: string
        catalogType?: { id: number; name: string }
        price: number
        sellingPrice?: number
        markupPercentage?: number
    }
    quantity: number
    unitPrice: number
    /**
     * Custo unitário congelado na venda (repasse ao parceiro + ao profissional).
     * `null` nos itens criados antes do campo existir — exibir "—", nunca zero.
     */
    unitCost?: number | null
    catalogVariationId?: number | null
    variationName?: string | null
    catalogVariation?: { id: number; name: string } | null
}

export interface Exam extends ServiceAddressSnapshot {
    id: number
    title: string
    // Quando o agendamento nasceu de um PACOTE, guarda o pacote de origem.
    // Null = exame comum. O title já é o nome comercial do pacote.
    packageCatalogId?: number | null
    packageCatalog?: { id: number; name: string } | null
    description?: string
    notes?: string
    status: ExamStatus
    requestedAt: string
    scheduledAt?: string
    completedAt?: string
    /** Cancelamento: o registro não é excluído, fica listado como Cancelado. */
    cancelledAt?: string | null
    cancellationReason?: string | null
    duration?: number
    anesthetistFee?: number | null
    validUntil?: string | null
    /** Vencimento da guia renovado manualmente pelo operador ("Renovar Guia"). */
    guideValidUntil?: string | null
    approvedAt?: string | null
    approvedBy?: number | null
    approver?: { id: number; name: string } | null
    totalPrice?: number
    discount?: number
    finalPrice?: number
    medicalSupplies?: {
        id: number
        medicalSupplyId: number
        medicalSupply: { id: number; name: string; type: string; unit: string; quantity: number; costPrice: number; sellingPrice?: number | null }
        quantity: number
        unitPrice: number
        notes?: string | null
    }[]
    clientId: number
    client?: {
        id: number
        name: string
        cpf?: string
        email?: string
        whatsAppNumber?: string
    }
    professionalId?: number
    professional?: {
        id: number
        name: string
        registrationNumber?: string
    }
    createdById?: number
    createdBy?: {
        id: number
        name: string
        email?: string
    }
    companyId: number
    partnerId?: number
    partner?: {
        id: number
        name: string
        tradingName?: string
    }
    healthPlanId?: number
    healthPlan?: { id: number; name: string; ansCode?: string }
    hospitalId?: number | null
    hospital?: {
        id: number
        name: string
        tradingName?: string | null
        cnpj?: string | null
    } | null
    attachments?: ExamAttachment[]
    metadata?: any
    items?: ExamItem[]
    results?: ExamResult[]
    agendaEvents?: {
        id: number
        title: string
        startDate: string
        endDate: string
        status: string
    }[]
    dealId?: number
    deal?: {
        id: number
        title: string
        status: string
        totalValue?: number
        stage: { id: number; name: string; color?: string }
        pipeline: { id: number; name: string; type: string }
        lead: { id: number; name: string; phoneNumber?: string; email?: string }
    }
    billingId?: number | null
    billing?: {
        id: number
        status: string
        amount: number
        /**
         * Coberto por convênio: `amount` é zero e o valor do atendimento está aqui.
         * É o que `netRevenue` usa — sem este campo, custo/venda/lucro da tela saem
         * com venda R$ 0,00 e lucro negativo. Ver billing-system.md §1.1.
         */
        invoicedAmount?: number | null
        payerType?: string | null
        healthPlan?: { id: number; name: string } | null
        paidAmount?: number | null
        paymentMethod?: string | null
        dueDate: string
        paidAt?: string | null
        publicToken?: string | null
        description?: string | null
        pixQrCodeBase64?: string | null
        pixCopyPaste?: string | null
        bankSlipUrl?: string | null
        barcode?: string | null
        digitableLine?: string | null
        paymentLink?: string | null
        /** Cobrança gerada no gateway: habilita a consulta ao provedor antes da baixa. */
        providerPaymentId?: string | null
        refundedAmount?: number | null
        refundedAt?: string | null
        refundedVia?: string | null
        /**
         * Rateio do repasse por atendimento. Presente quando a cobrança cobre mais
         * de um (o paciente pagou a visita inteira de uma vez): aí os valores desta
         * tela têm de sair da FATIA, não da cobrança, que vale pelos outros também.
         * Ver docs/billing-payout-share.md.
         */
        payoutShares?: PayoutShare[] | null
    } | null
    createdAt: string
    updatedAt: string
}

export interface CreateExamData {
    /**
     * Agendar sem gerar cobrança agora — o paciente paga no dia, na recepção.
     *
     * O valor do exame continua gravado; só a emissão espera, e sai depois pelo "Gerar
     * cobrança" da tela de detalhe. Não confundir com `requestOnly` (pedido médico, que
     * nasce SEM valor) nem com o orçamento, que ainda não foi aprovado.
     */
    deferBilling?: boolean
    /**
     * Local de atendimento confirmado no agendamento (snapshot de auditoria).
     * Sem isso, o backend deriva de parceiro > profissional.
     */
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    addressSource?: 'PARTNER' | 'PROFESSIONAL' | 'HOSPITAL' | 'CUSTOM' | 'UNCONFIRMED'
    title?: string
    description?: string
    notes?: string
    status?: ExamStatus
    /**
     * Pedido médico: nasce sem valor e sem cobrança, ainda que o item do catálogo
     * tenha preço. Instrução de criação — o registro não guarda essa marca, e o
     * mesmo exame pode ser vendido depois pelo agendamento comum.
     */
    requestOnly?: boolean
    scheduledAt?: string
    duration?: number
    clientId: number
    professionalId?: number
    companyId: number
    partnerId?: number
    healthPlanId?: number
    hospitalId?: number
    /**
     * Cobrança em aberto do paciente onde SOMAR este exame, em vez de gerar
     * cobrança própria — quem agenda em vários lugares paga uma vez só.
     */
    billingTargetId?: number
    agendaId?: number
    roomId?: number
    attachments?: any
    metadata?: any
    items: { catalogId: number; quantity: number; variationCatalogIds?: number[] }[]
    discount?: number
    paymentMethod?: string
    pipelineId?: number
    anesthetistFee?: number
    validUntil?: string
    medicalSupplies?: { medicalSupplyId: number; quantity: number; unitPrice: number; notes?: string }[]
    /** Por ordem de chegada: tem data, não tem hora marcada. */
    arrivalOrder?: boolean
}

export interface ApproveBudgetData {
    /** Local de atendimento confirmado na aprovação (snapshot no agendamento). */
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    addressSource?: 'PARTNER' | 'PROFESSIONAL' | 'HOSPITAL' | 'CUSTOM' | 'UNCONFIRMED'
    scheduledAt?: string
    agendaId?: number
    roomId?: number
    paymentMethod?: string
    pipelineId?: number
}

export interface UpdateExamData
    extends Partial<
        Omit<CreateExamData, 'professionalId' | 'partnerId' | 'healthPlanId'>
    > {
    /**
     * `null` RETIRA o executante. Sem o null, o campo ausente significa "não mexer" e
     * o profissional removido na tela continuava gravado.
     */
    professionalId?: number | null
    partnerId?: number | null
    /** `null` RETIRA o convênio. Ausente significa "não mexer neste campo". */
    healthPlanId?: number | null
    /**
     * Cria a cobrança do atendimento quando ele ainda não tem uma. Enviado só quando o
     * operador mexe no bloco de pagamento — editar apenas a data não pode gerar cobrança.
     */
    generateBilling?: boolean
}

export interface CreateExamResultData {
    catalogId: number
    result?: string
    observations?: string
    professionalId: number
    attachments?: any
}

export interface UpdateExamResultData {
    result?: string
    observations?: string
    professionalId?: number
}

export async function getExams(
    companyId?: number
): Promise<ApiResponse<Exam[]>> {
    const params = companyId ? { companyId } : {}
    return apiRequest<Exam[]>('exams', { params })
}

/**
 * @param params.packages 'exclude' esconde os exames vindos de pacote (eles têm a tela
 *                        /medical/packages); 'only' devolve apenas eles.
 */
export async function getExamsByCompany(
    companyId: number,
    params?: { page?: number; limit?: number; search?: string; status?: string; partnerId?: number; id?: number; cpf?: string; clientId?: number; createdById?: number; packages?: 'exclude' | 'only'; startDate?: string; endDate?: string; sortBy?: string; sortOrder?: string;
        /**
         * Janela de DATA DE PAGAMENTO (`billing.paidAt`), independente de
         * `startDate`/`endDate`, que filtram o registro. É o que o fechamento de caixa
         * usa: a venda de sexta paga na segunda pertence ao caixa da segunda.
         */
        paidStartDate?: string; paidEndDate?: string;
        /** Status da cobrança vinculada; 'NONE' = sem cobrança. */
        billingStatus?: string;
    }
): Promise<ApiResponse<Exam[]>> {
    return apiRequest<Exam[]>(`exams/company/${companyId}`, { params: params || {} })
}

export async function getExamsByClient(
    clientId: number
): Promise<ApiResponse<Exam[]>> {
    return apiRequest<Exam[]>(`exams/client/${clientId}`, {})
}

export async function getExamsByProfessional(
    professionalId: number
): Promise<ApiResponse<Exam[]>> {
    return apiRequest<Exam[]>(`exams/professional/${professionalId}`, {})
}

export async function getExam(id: number): Promise<ApiResponse<Exam>> {
    return apiRequest<Exam>(`exams/${id}`, {})
}

export async function createExam(
    data: CreateExamData
): Promise<ApiResponse<Exam>> {
    return apiRequest<Exam>('exams', { data }, 'post')
}

export async function updateExam(
    id: number,
    data: UpdateExamData
): Promise<ApiResponse<Exam>> {
    return apiRequest<Exam>(`exams/${id}`, { data }, 'patch')
}

/**
 * Troca o local de atendimento já gravado no exame.
 *
 * Endpoint próprio, e não o `updateExam`: a edição arrasta recálculo de preço e
 * cobrança complementar, que não têm nada a ver com corrigir uma rua.
 */
export async function updateExamServiceAddress(
    id: number,
    data: ServiceAddressSnapshot
): Promise<ApiResponse<Exam>> {
    return apiRequest<Exam>(`exams/${id}/service-address`, { data }, 'patch')
}

export async function deleteExam(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`exams/${id}`, {}, 'delete')
}

export async function uploadExamAttachment(
    id: number,
    file: File,
    userName: string,
    description: string,
    details?: string
): Promise<ApiResponse<Exam>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('userName', userName)
    formData.append('description', description)
    if (details) {
        formData.append('details', details)
    }

    const client = getClient(true)
    // A chave 'token' nunca existiu neste projeto — estas chamadas iam sem
    // credencial. O access token vive em memória.
    const token = getStoredToken()

    const response = await client.post(`exams/${id}/attachments`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
        },
    })

    return {
        success: true,
        result: response.data,
    }
}

export async function deleteExamAttachment(
    id: number,
    attachmentIndex: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`exams/${id}/attachments/${attachmentIndex}`, {}, 'delete')
}

export async function addExamResult(
    examId: number,
    data: CreateExamResultData
): Promise<ApiResponse<ExamResult>> {
    return apiRequest<ExamResult>(`exams/${examId}/results`, { data }, 'post')
}

export async function updateExamResult(
    examId: number,
    resultId: number,
    data: UpdateExamResultData
): Promise<ApiResponse<ExamResult>> {
    return apiRequest<ExamResult>(
        `exams/${examId}/results/${resultId}`,
        { data },
        'patch'
    )
}

export async function deleteExamResult(
    examId: number,
    resultId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`exams/${examId}/results/${resultId}`, {}, 'delete')
}

export async function uploadExamResultAttachment(
    examId: number,
    resultId: number,
    file: File,
    userName: string,
    description: string,
    details?: string
): Promise<ApiResponse<ExamResult>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('userName', userName)
    formData.append('description', description)
    if (details) {
        formData.append('details', details)
    }

    const client = getClient(true)
    // A chave 'token' nunca existiu neste projeto — estas chamadas iam sem
    // credencial. O access token vive em memória.
    const token = getStoredToken()

    const response = await client.post(
        `exams/${examId}/results/${resultId}/attachments`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`,
            },
        }
    )

    return {
        success: true,
        result: response.data,
    }
}

export async function printExamResults(
    examId: number,
    delivery: PdfDelivery = 'download'
): Promise<void> {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const token = getStoredToken() || ''

    const url = `${baseUrl}/exams/${examId}/results/pdf`

    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!response.ok) throw new Error('Erro ao exportar resultados em PDF')

    const blob = await response.blob()
    const filename = `resultados-exame-${examId}.pdf`

    deliverPdf(blob, filename, delivery)
}

export async function printExamOrder(
    examId: number,
    delivery: PdfDelivery = 'download'
): Promise<void> {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const token = getStoredToken() || ''

    const url = `${baseUrl}/exams/${examId}/order/pdf`

    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!response.ok) throw new Error('Erro ao exportar pedido de exame em PDF')

    const blob = await response.blob()
    const filename = `pedido-exame-${examId}.pdf`

    deliverPdf(blob, filename, delivery)
}

// === BILLING FUNCTIONS ===

export async function markExamBillingAsPaid(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`exams/${id}/billing/mark-paid`, {}, 'patch')
}

export async function cancelExamBilling(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`exams/${id}/billing/cancel`, {}, 'patch')
}

/**
 * Resultado do cancelamento de um atendimento.
 *
 * `refundPending` diz que a cobrança já estava paga: o exame foi cancelado, mas o
 * dinheiro só volta depois que um operador financeiro aprovar o estorno. A tela
 * precisa dizer isso a quem cancelou — senão o usuário assume que o reembolso saiu.
 */
export interface CancelAppointmentResult {
    refundPending: boolean
    requestId?: number
}

/**
 * Cancela o exame sem excluí-lo: ele continua na listagem com status "Cancelado".
 * A cobrança sem pagamento é cancelada junto; a cobrança paga vira solicitação de
 * estorno.
 */
export async function cancelExam(
    id: number,
    reason?: string,
): Promise<ApiResponse<CancelAppointmentResult>> {
    return apiRequest<CancelAppointmentResult>(
        `exams/${id}/cancel`,
        { data: { reason } },
        'patch',
    )
}

/** Transfere o vendedor (createdBy) do exame para outro usuário. */
export async function transferExamSeller(
    id: number,
    sellerId: number,
): Promise<ApiResponse<Exam>> {
    return apiRequest<Exam>(
        `exams/${id}/transfer-seller`,
        { data: { sellerId } },
        'patch',
    )
}

/** Gera a cobrança de um exame que ficou sem cobrança. Retorna o id da cobrança criada. */
export async function generateExamBilling(
    id: number,
    /**
     * Quanto cobrar agora, em centavos. Ausente = o saldo inteiro.
     *
     * Vai no gesto de CRIAR porque alterar o valor de uma cobrança atrelada a
     * atendimento é proibido — é por ali que sairia desconto sem autorização.
     */
    amount?: number,
): Promise<ApiResponse<{ billingId: number }>> {
    return apiRequest<{ billingId: number }>(
        `exams/${id}/generate-billing`,
        amount != null ? { data: { amount } } : {},
        'post',
    )
}

export const EXAM_STATUS_LABELS: Record<ExamStatus, string> = {
    BUDGET: 'Orçamento',
    REQUESTED: 'Solicitado',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
}

export const EXAM_STATUS_COLORS: Record<ExamStatus, string> = {
    BUDGET: 'badge-accent',
    REQUESTED: 'badge-warning',
    IN_PROGRESS: 'badge-info',
    COMPLETED: 'badge-success',
    CANCELLED: 'badge-error',
}

/**
 * Exame solicitado só é "Agendado" depois que a data é marcada; até lá continua
 * como solicitação.
 */
export function getExamStatusLabel(
    status: ExamStatus,
    scheduledAt?: string | null,
): string {
    if (status === 'REQUESTED' && scheduledAt) return 'Agendado'
    return EXAM_STATUS_LABELS[status]
}

export function getExamStatusColor(
    status: ExamStatus,
    scheduledAt?: string | null,
): string {
    if (status === 'REQUESTED' && scheduledAt) return 'badge-info'
    return EXAM_STATUS_COLORS[status]
}

// ==================== ORÇAMENTO ====================

export async function approveExamBudget(
    examId: number,
    data: ApproveBudgetData,
): Promise<ApiResponse<Exam>> {
    return apiRequest<Exam>(`exams/${examId}/approve-budget`, { data }, 'patch')
}

/** Renova a validade da guia: vencimento = hoje + dias configurados na empresa. */
export async function renewExamGuide(examId: number): Promise<ApiResponse<Exam>> {
    return apiRequest<Exam>(`exams/${examId}/renew-guide`, {}, 'patch')
}

export interface ImportExamListMatchedItem {
    catalogId: number
    name: string
    quantity: number
    unitPrice: number
    confidence: 'exact' | 'fuzzy' | 'semantic'
    extractedName: string
}

export interface ImportExamListResult {
    matched: ImportExamListMatchedItem[]
    unmatched: { name: string; quantity: number }[]
    rawExtracted: { name: string; quantity?: number }[]
    notes?: string
}

export async function importExamListFromDocument(
    file: File,
): Promise<ApiResponse<ImportExamListResult>> {
    const formData = new FormData()
    formData.append('file', file)

    const client = getClient(true)
    // A chave 'token' nunca existiu neste projeto — estas chamadas iam sem
    // credencial. O access token vive em memória.
    const token = getStoredToken()

    try {
        const response = await client.post(
            'exams/import-from-document',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                },
            },
        )
        return {
            success: true,
            result: response.data as ImportExamListResult,
        }
    } catch (err: any) {
        return {
            success: false,
            result: undefined as any,
            error:
                err?.response?.data?.message ||
                err?.message ||
                'Erro ao processar documento',
        }
    }
}

export async function exportExamBudgetPdf(
    examId: number,
    delivery: PdfDelivery = 'download'
): Promise<void> {
    const client = getClient()
    const response = await client.get(`exams/${examId}/budget/pdf`, { responseType: 'blob' })
    deliverPdf(
        new Blob([response.data], { type: 'application/pdf' }),
        `orcamento-exame-${examId}.pdf`,
        delivery
    )
}

/**
 * Envia a GUIA do pedido ao paciente — o documento que ele leva ao laboratório,
 * SEM valores. Não confundir com `sendExamBudgetEmail`, que manda o orçamento,
 * com preços, para quem decide comprar.
 */
export async function sendExamOrderEmail(examId: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`exams/${examId}/send-email`, {}, 'post')
}

export async function sendExamBudgetEmail(examId: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`exams/${examId}/budget/send-email`, {}, 'post')
}

export interface RescheduleAppointmentData {
    /** ISO. Com `arrivalOrder`, a hora é descartada no backend. */
    scheduledAt: string
    /** Por ordem de chegada: tem data, não tem hora. */
    arrivalOrder?: boolean
    reason?: string
}

/**
 * Remarca o atendimento: move a entidade E a agenda juntas, valida conflito de horário
 * (empresas de INTERMEDIAÇÃO não bloqueiam), grava o histórico e avisa o paciente da nova
 * data.
 *
 * Diferente de um `update` com data nova — que só sobrescrevia o campo, sem checar
 * conflito e sem avisar ninguém.
 */
export async function rescheduleExam(
    id: number,
    data: RescheduleAppointmentData
): Promise<ApiResponse<Exam>> {
    return apiRequest<Exam>(`exams/${id}/reschedule`, { data }, 'patch')
}

/**
 * REMOVE a data do atendimento: o horário é liberado (o `AgendaEvent` é apagado) e o
 * status acompanha.
 *
 * O exame continua existindo — some da agenda e volta para SOLICITADO. Não
 * confundir com cancelar: cobrança, itens e repasses ficam intactos.
 */
export async function unscheduleExam(
    id: number,
    data: { reason?: string } = {}
): Promise<ApiResponse<Exam>> {
    return apiRequest<Exam>(`exams/${id}/unschedule`, { data }, 'patch')
}

/**
 * Troca o status manualmente — a régua do operador sobre o que de fato aconteceu.
 * Só o status (e o evento da agenda) mudam: nada de cobrança, repasse ou aviso ao
 * paciente. `CANCELLED` é recusado pelo backend; cancelar tem ação própria.
 */
export async function changeExamStatus(
    id: number,
    status: ExamStatus,
): Promise<ApiResponse<Exam>> {
    return apiRequest<Exam>(`exams/${id}/status`, { data: { status } }, 'patch')
}

/** Item do exame na separação do repasse ao parceiro, com o valor dele. */
export interface PayoutItemSplitPiece {
    itemId: number
    label: string
    amount: number
}

export interface PayoutItemSplitPreview {
    /** `false` traz o motivo em `reason`, na linguagem do operador. */
    canSplit: boolean
    reason: string | null
    payableId: number | null
    payableAmount: number | null
    pieces: PayoutItemSplitPiece[]
}

/**
 * O que a separação por item faria com o repasse ao parceiro deste exame, na cobrança
 * informada. A regra é uma conta por exame; separar é para parceiro que cobra cada item
 * à parte.
 */
export async function getExamPayoutItemSplit(
    examId: number,
    billingId: number
): Promise<ApiResponse<PayoutItemSplitPreview>> {
    return apiRequest<PayoutItemSplitPreview>(`exams/${examId}/payout-split`, {
        params: { billingId },
    })
}

/** Divide a conta a pagar do repasse ao parceiro deste exame em uma conta por item. */
export async function splitExamPayoutByItem(
    examId: number,
    billingId: number
): Promise<ApiResponse<{ payableIds: number[] }>> {
    return apiRequest<{ payableIds: number[] }>(
        `exams/${examId}/payout-split?billingId=${billingId}`,
        {},
        'post'
    )
}
